// referenceMediaApi.js
//
// Reuses the SAME generic "document = folder of files" primitive the kindergarten news/media
// gallery already use (models/docstorage.js, docs_files collection, PUT /v1/docfile) — with our
// own `target` value instead of 'KG', so photos on a product-item or a dish are stored the same
// way and never confused with a kindergarten's own docs. See docstore.customTarget.patch.md for
// the backend changes this needed.
//
// Video is NOT uploaded as a file here — same as the kindergarten gallery, it's a YouTube link
// (source: 'youtube' in the same files[] array) added via addYoutubeVideo below.
//
// Preview: like NewsEditor's cover image, an uploaded file is shown via a direct
// `docfile?doc_id=..&file_id=..` URL — no blob-fetching needed, the browser renders it inline
// in <img> regardless of the endpoint's Content-Disposition header.
import apiClient, { apiSendFileHeader, constr_uri } from '../../services/apiClient';

// Mirrors newsAPI.js's base64ToFile — FileUploader (asBase64) hands back picked files as
// { type, name, size, file: <base64 data URL> }; the upload endpoint needs a real File/Blob.
export const base64ToFile = (dataUrl, filename, mtype) => {
  const arr = dataUrl.split(',');
  const mimeMatch = arr[0].match(/:(.*?);/);
  const mime = mtype || (mimeMatch ? mimeMatch[1] : 'application/octet-stream');
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) u8arr[n] = bstr.charCodeAt(n);
  return new File([u8arr], filename, { type: mime });
};

// target: 'product-item' | 'dish'
export const referenceMediaApi = {
  findContainer: async (target, targetId) => {
    const result = await apiClient.get(`kg_docfile_info?kg_id=${targetId}&type=media_gallery`);
    const list = result.data || result || [];
    return Array.isArray(list) && list.length > 0 ? list[0] : null;
  },

  createContainer: async (target, targetId, name) => {
    const result = await apiClient.post('kg_docinfo', {
      target_id: targetId,
      target,
      type: 'media_gallery',
      name
    });
    return result.data || result;
  },

  ensureContainer: async (target, targetId, name) => {
    const existing = await referenceMediaApi.findContainer(target, targetId);
    if (existing) return existing;
    return referenceMediaApi.createContainer(target, targetId, name);
  },

  uploadFile: async (containerId, file) => {
    const buffer = await file.arrayBuffer();
    const fileInfo = {
      doc_id: containerId,
      name: file.name,
      mtype: file.type,
      length: file.size,
      date: new Date().toISOString()
    };
    const response = await apiSendFileHeader('docfile', buffer, fileInfo);
    const json = await response.json().catch(() => ({}));
    if (!response.ok || json.error) {
      throw new Error((json.error && json.error.message) || `Помилка завантаження файлу (${response.status})`);
    }
    return json.data;
  },

  removeFile: async (containerId, fileId) => {
    const result = await apiClient.post('file_to_trash', { id_doc: containerId, id_file: fileId });
    return result.data || result;
  },

  // Video — same as the kindergarten gallery: a YouTube link, not an uploaded file (see
  // YoutubeAddForm.jsx precedent). Used for dishes only.
  getYoutubeOembed: async (url) => {
    const result = await apiClient.get(`youtube_oembed?url=${encodeURIComponent(url)}`);
    return result.data || result;
  },

  addYoutubeVideo: async (containerId, url, meta) => {
    const m = meta || {};
    const result = await apiClient.post('youtube_link', {
      id_doc: containerId,
      url,
      caption: m.caption || ''
    });
    return result.data || result;
  },

  removeYoutubeVideo: async (containerId, fileId) => {
    const result = await apiClient.post('youtube_link_remove', { id_doc: containerId, id_file: fileId });
    return result.data || result;
  },

  // Same direct-URL pattern NewsEditor uses for its cover image.
  fileUrl: (containerId, fileId) => constr_uri(`docfile?doc_id=${containerId}&file_id=${fileId}`)
};

export default referenceMediaApi;